import React, { useEffect, useState } from 'react';
import { logout } from '../lib/firebase.js';
import { API_BASE_URL } from '../lib/apiBase';
import { HamburgerMenu } from './HamburgerMenu';

interface DashboardProps {
  user: any;
  onJoinRoom: (roomInfo: { roomId: string; nickname: string; roomName: string }) => void;
}

interface UserRoomInfo {
  roomId: string;
  roomName: string;
  nickname: string;
  isBlocked: boolean;
  showReadReceipt: boolean;
  announcement?: string;
  lastSeq: number;
  myReadSeq: number;
  unreadCount: number;
  participants: string[];
  ownerUserId?: string;
  ownerNickname?: string;
  guestMessageLimit?: number;
  consecutiveGuestMessages?: number;
  templates?: string[];
  createdAt: string;
  isOrphaned?: boolean;
}

export const Dashboard: React.FC<DashboardProps> = ({ user, onJoinRoom }) => {
  const [rooms, setRooms] = useState<UserRoomInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [activeModal, setActiveModal] = useState<
    'create' | 'join' | 'settings' | 'bannedWords' | 'templates' | 'bulkSettings' | null
  >(null);

  // 部屋一覧の複数選択（一括設定用）。オーナーのルームのみ選択可能。
  const [selectedRoomIds, setSelectedRoomIds] = useState<Set<string>>(new Set());

  // 一括設定モーダルの入力状態
  const [bulkApplyAnnouncement, setBulkApplyAnnouncement] = useState(false);
  const [bulkAnnouncement, setBulkAnnouncement] = useState('');
  const [bulkApplyGuestLimit, setBulkApplyGuestLimit] = useState(false);
  const [bulkGuestLimit, setBulkGuestLimit] = useState<string>('0');
  const [bulkResult, setBulkResult] = useState<{
    updatedRoomCount: number;
    failures: Array<{ roomId: string; reason: string }>;
  } | null>(null);
  const [roomNameInput, setRoomNameInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [nicknameInput, setNicknameInput] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Selected room for settings
  const [selectedRoom, setSelectedRoom] = useState<UserRoomInfo | null>(null);
  const [settingBlocked, setSettingBlocked] = useState(false);
  const [settingShowReceipt, setSettingShowReceipt] = useState(true);
  const [settingAnnouncement, setSettingAnnouncement] = useState('');
  const [settingGuestLimit, setSettingGuestLimit] = useState<string>('0');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // グローバルNGワード設定（全所有ルームに適用）。モーダルを開いた時に
  // サーバから現在値を取得し、保存時に PUT する。
  const [globalBannedWords, setGlobalBannedWords] = useState<string>('');
  const [bannedWordsLoading, setBannedWordsLoading] = useState(false);

  // グローバルテンプレートメッセージ設定（全所有ルームに適用）。
  // 各テンプレートは複数行のテキスト1件として保持し、リスト＋編集ペインで
  // 個別に追加・編集・削除する。
  const [globalTemplates, setGlobalTemplates] = useState<string[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  // 編集中のインデックス: null = 何も編集していない、-1 = 新規追加中、
  // 0..n = 既存テンプレートを編集中
  const [editingTemplateIndex, setEditingTemplateIndex] = useState<number | null>(null);
  const [editingTemplateText, setEditingTemplateText] = useState<string>('');
  // 削除確認用: 確認 UI 表示中の対象 index
  const [deleteTemplateIndex, setDeleteTemplateIndex] = useState<number | null>(null);

  const fetchRooms = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/user/rooms`, {
        headers: {
          'X-Firebase-Token': token,
        },
      });

      if (!res.ok) {
        throw new Error(`エラーが発生しました: ${res.status}`);
      }

      const data = await res.json();
      setRooms(data);
    } catch (err: any) {
      console.error(err);
      setError(err.message || '部屋一覧の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, [user]);

  const handleOpenSettings = (room: UserRoomInfo) => {
    setSelectedRoom(room);
    setSettingBlocked(room.isBlocked);
    setSettingShowReceipt(room.showReadReceipt);
    setSettingAnnouncement(room.announcement || '');
    setSettingGuestLimit(String(room.guestMessageLimit ?? 0));
    setShowDeleteConfirm(false);
    setActiveModal('settings');
  };

  const handleOpenBannedWords = async () => {
    setActionError(null);
    setBannedWordsLoading(true);
    setActiveModal('bannedWords');
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/user/bannedWords`, {
        headers: { 'X-Firebase-Token': token },
      });
      if (!res.ok) {
        throw new Error('NGワード設定の取得に失敗しました。');
      }
      const data = (await res.json()) as { bannedWords?: string[] };
      setGlobalBannedWords((data.bannedWords ?? []).join('\n'));
    } catch (err: any) {
      console.error(err);
      setActionError(err.message || 'NGワード設定の取得に失敗しました。');
      setGlobalBannedWords('');
    } finally {
      setBannedWordsLoading(false);
    }
  };

  /**
   * テンプレート設定モーダルを開く。サーバから現在のテンプレート一覧を取得し、
   * 内部状態 `globalTemplates` をシードする。編集中・削除中の状態はリセット。
   */
  const handleOpenTemplates = async () => {
    setActionError(null);
    setTemplatesLoading(true);
    setEditingTemplateIndex(null);
    setEditingTemplateText('');
    setDeleteTemplateIndex(null);
    setActiveModal('templates');
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/user/templates`, {
        headers: { 'X-Firebase-Token': token },
      });
      if (!res.ok) {
        throw new Error('テンプレート設定の取得に失敗しました。');
      }
      const data = (await res.json()) as { templates?: string[] };
      setGlobalTemplates(data.templates ?? []);
    } catch (err: any) {
      console.error(err);
      setActionError(err.message || 'テンプレート設定の取得に失敗しました。');
      setGlobalTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  /**
   * 「新規追加」ボタン押下時。-1 を使って「新規追加中」を表現し、編集ペインを
   * 開く。既存の編集状態は破棄する。
   */
  const handleStartAddTemplate = () => {
    setActionError(null);
    setEditingTemplateIndex(-1);
    setEditingTemplateText('');
    setDeleteTemplateIndex(null);
  };

  /**
   * 既存テンプレートの編集を開始。リストアイテムの「編集」ボタンから呼ばれる。
   */
  const handleStartEditTemplate = (index: number) => {
    setActionError(null);
    setEditingTemplateIndex(index);
    setEditingTemplateText(globalTemplates[index] ?? '');
    setDeleteTemplateIndex(null);
  };

  /** 編集ペインのキャンセル。状態を破棄してリスト表示に戻す。 */
  const handleCancelEditTemplate = () => {
    setEditingTemplateIndex(null);
    setEditingTemplateText('');
  };

  /**
   * 編集ペインの「適用」ボタン。クライアント側でバリデーション後、
   * `globalTemplates` を差し替える。サーバへは「保存」ボタンで一括反映する。
   */
  const handleApplyEditingTemplate = () => {
    const trimmed = editingTemplateText.trim();
    if (trimmed.length === 0) {
      setActionError('テンプレート本文を入力してください。');
      return;
    }
    if (trimmed.length > 2000) {
      setActionError('テンプレートは2000文字以内で入力してください。');
      return;
    }
    // 完全一致重複の検出（編集中アイテム自身は除外）
    const dupIndex = globalTemplates.findIndex((t, i) =>
      i !== editingTemplateIndex && t === trimmed,
    );
    if (dupIndex >= 0) {
      setActionError('同じ内容のテンプレートが既に登録されています。');
      return;
    }

    setActionError(null);
    if (editingTemplateIndex === -1) {
      // 新規追加
      if (globalTemplates.length >= 50) {
        setActionError('テンプレートは最大50件までです。');
        return;
      }
      setGlobalTemplates((prev) => [...prev, trimmed]);
    } else if (editingTemplateIndex !== null) {
      // 既存編集
      setGlobalTemplates((prev) =>
        prev.map((t, i) => (i === editingTemplateIndex ? trimmed : t)),
      );
    }
    setEditingTemplateIndex(null);
    setEditingTemplateText('');
  };

  /** 削除確認 UI を開く。 */
  const handleAskDeleteTemplate = (index: number) => {
    setActionError(null);
    setDeleteTemplateIndex(index);
  };

  /** 削除確認 UI を閉じる。 */
  const handleCancelDeleteTemplate = () => {
    setDeleteTemplateIndex(null);
  };

  /**
   * テンプレートを削除（クライアント側のリストから除去）。サーバ反映は
   * 「保存」ボタンで一括行う。
   */
  const handleConfirmDeleteTemplate = () => {
    if (deleteTemplateIndex === null) return;
    setGlobalTemplates((prev) =>
      prev.filter((_, i) => i !== deleteTemplateIndex),
    );
    setDeleteTemplateIndex(null);
    // 編集中だったテンプレートが削除されたら編集状態もクリア
    if (editingTemplateIndex === deleteTemplateIndex) {
      setEditingTemplateIndex(null);
      setEditingTemplateText('');
    }
  };

  /**
   * 現在の `globalTemplates` をサーバに保存（一括 PUT）。サーバ側は全所有
   * ルームに複製し、SignalR `templatesChanged` を配信する。
   */
  const handleSaveTemplates = async () => {
    if (editingTemplateIndex !== null) {
      setActionError('編集中のテンプレートを適用またはキャンセルしてください。');
      return;
    }
    setActionLoading(true);
    setActionError(null);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/user/templates`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Firebase-Token': token,
        },
        body: JSON.stringify({ templates: globalTemplates }),
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || 'テンプレート設定の保存に失敗しました。');
      }
      setActiveModal(null);
    } catch (err: any) {
      console.error(err);
      setActionError(err.message || 'テンプレート設定の保存に失敗しました。');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveBannedWords = async () => {
    setActionLoading(true);
    setActionError(null);

    // 改行区切り→trim/空行除去/長さ制限/重複除去
    const seenWord = new Set<string>();
    const words: string[] = [];
    let overlong = false;
    for (const raw of globalBannedWords.split(/\r?\n/)) {
      const trimmed = raw.trim();
      if (trimmed.length === 0) continue;
      if (trimmed.length > 50) {
        overlong = true;
        break;
      }
      const key = trimmed.toLowerCase();
      if (seenWord.has(key)) continue;
      seenWord.add(key);
      words.push(trimmed);
    }
    if (overlong) {
      setActionError('NGワードは1件あたり50文字以内で入力してください。');
      setActionLoading(false);
      return;
    }
    if (words.length > 50) {
      setActionError('NGワードは最大50件までです。');
      setActionLoading(false);
      return;
    }

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/user/bannedWords`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Firebase-Token': token,
        },
        body: JSON.stringify({ bannedWords: words }),
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || 'NGワード設定の保存に失敗しました。');
      }
      setActiveModal(null);
    } catch (err: any) {
      console.error(err);
      setActionError(err.message || 'NGワード設定の保存に失敗しました。');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roomNameInput.trim() || !passwordInput.trim() || !nicknameInput.trim()) {
      setActionError('すべての項目を入力してください。');
      return;
    }

    setActionLoading(true);
    setActionError(null);

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/user/rooms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Firebase-Token': token,
        },
        body: JSON.stringify({
          roomName: roomNameInput.trim(),
          password: passwordInput.trim(),
          nickname: nicknameInput.trim(),
          mode: 'create',
        }),
      });

      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || '部屋の作成に失敗しました。');
      }

      // Reset form
      setRoomNameInput('');
      setPasswordInput('');
      setNicknameInput('');
      setActiveModal(null);
      fetchRooms();
    } catch (err: any) {
      console.error(err);
      setActionError(err.message || '部屋の作成中にエラーが発生しました。');
    } finally {
      setActionLoading(false);
    }
  };

  const handleJoinRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roomNameInput.trim() || !passwordInput.trim() || !nicknameInput.trim()) {
      setActionError('すべての項目を入力してください。');
      return;
    }

    setActionLoading(true);
    setActionError(null);

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/user/rooms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Firebase-Token': token,
        },
        body: JSON.stringify({
          roomName: roomNameInput.trim(),
          password: passwordInput.trim(),
          nickname: nicknameInput.trim(),
          mode: 'join',
        }),
      });

      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || '部屋への参加に失敗しました。');
      }

      // Reset form
      setRoomNameInput('');
      setPasswordInput('');
      setNicknameInput('');
      setActiveModal(null);
      fetchRooms();
    } catch (err: any) {
      console.error(err);
      setActionError(err.message || '部屋への参加中にエラーが発生しました。');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!selectedRoom) return;

    setActionLoading(true);
    setActionError(null);

    try {
      const token = await user.getIdToken();
      const updatePayload: any = {
        isBlocked: settingBlocked,
        showReadReceipt: settingShowReceipt,
      };

      // Only include owner-only fields if owner
      const isOwner = selectedRoom.ownerUserId === user.uid;
      if (isOwner) {
        updatePayload.announcement = settingAnnouncement.trim();

        const parsedLimit = Number.parseInt(settingGuestLimit, 10);
        if (
          !Number.isFinite(parsedLimit) ||
          parsedLimit < 0 ||
          parsedLimit > 100
        ) {
          setActionError(
            'ゲストの連続メッセージ制限は 0〜100 の整数で指定してください。',
          );
          setActionLoading(false);
          return;
        }
        updatePayload.guestMessageLimit = parsedLimit;
      }

      const res = await fetch(`${API_BASE_URL}/api/user/rooms/${selectedRoom.roomId}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Firebase-Token': token,
        },
        body: JSON.stringify(updatePayload),
      });

      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || '設定の更新に失敗しました。');
      }

      setActiveModal(null);
      setSelectedRoom(null);
      fetchRooms();
    } catch (err: any) {
      console.error(err);
      setActionError(err.message || '設定の保存中にエラーが発生しました。');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteRoom = async () => {
    if (!selectedRoom) return;

    setActionLoading(true);
    setActionError(null);

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/user/rooms/${selectedRoom.roomId}`, {
        method: 'DELETE',
        headers: {
          'X-Firebase-Token': token,
        },
      });

      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || '部屋の削除に失敗しました。');
      }

      setActiveModal(null);
      setSelectedRoom(null);
      setShowDeleteConfirm(false);
      fetchRooms();
    } catch (err: any) {
      console.error(err);
      setActionError(err.message || '部屋の削除中にエラーが発生しました。');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRoomClick = (room: UserRoomInfo) => {
    if (room.isBlocked) return;
    onJoinRoom({
      roomId: room.roomId,
      nickname: room.nickname,
      roomName: room.roomName,
    });
  };

  /**
   * 部屋一覧チェックボックスのトグル。オーナーでないルームは選択不可。
   * 部屋カード本体のクリック (チャット入室) と独立して扱うため、呼び出し側で
   * `stopPropagation` を行う。
   */
  const handleToggleRoomSelection = (room: UserRoomInfo) => {
    const isOwner = room.ownerUserId === user.uid;
    if (!isOwner) return;
    setSelectedRoomIds((prev) => {
      const next = new Set(prev);
      if (next.has(room.roomId)) {
        next.delete(room.roomId);
      } else {
        next.add(room.roomId);
      }
      return next;
    });
  };

  /** 一覧の選択を全解除。 */
  const handleClearSelection = () => {
    setSelectedRoomIds(new Set());
  };

  /** 自分がオーナーの全ルームを一括選択。 */
  const handleSelectAllOwned = () => {
    const ownedIds = rooms
      .filter((r) => r.ownerUserId === user.uid)
      .map((r) => r.roomId);
    setSelectedRoomIds(new Set(ownedIds));
  };

  /**
   * 一括設定モーダルを開く。選択中の最初のルームの値を初期値にして
   * 「現状を踏まえて編集」しやすくする。各フィールドは「適用するかどうか」を
   * チェックボックスで切り替える明示的 UX とする (誤って全ルームの値を
   * 一律ゼロに上書きする事故を防ぐため)。
   */
  const handleOpenBulkSettings = () => {
    if (selectedRoomIds.size === 0) return;
    const firstSelected = rooms.find((r) => selectedRoomIds.has(r.roomId));
    setBulkApplyAnnouncement(false);
    setBulkAnnouncement(firstSelected?.announcement ?? '');
    setBulkApplyGuestLimit(false);
    setBulkGuestLimit(String(firstSelected?.guestMessageLimit ?? 0));
    setBulkResult(null);
    setActionError(null);
    setActiveModal('bulkSettings');
  };

  /**
   * 一括設定の保存。`/api/user/rooms/bulk-settings` を1回叩いて全選択ルームに
   * 適用する。失敗ルームがあった場合はモーダル内に件数表示し、ユーザが内容を
   * 確認できるようにする。
   */
  const handleSaveBulkSettings = async () => {
    if (selectedRoomIds.size === 0) return;
    if (!bulkApplyAnnouncement && !bulkApplyGuestLimit) {
      setActionError('適用する項目を1つ以上選択してください。');
      return;
    }

    const payload: {
      roomIds: string[];
      announcement?: string;
      guestMessageLimit?: number;
    } = {
      roomIds: Array.from(selectedRoomIds),
    };

    if (bulkApplyAnnouncement) {
      payload.announcement = bulkAnnouncement.trim();
    }
    if (bulkApplyGuestLimit) {
      const parsed = Number.parseInt(bulkGuestLimit, 10);
      if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
        setActionError(
          'ゲストの連続メッセージ制限は 0〜100 の整数で指定してください。',
        );
        return;
      }
      payload.guestMessageLimit = parsed;
    }

    setActionLoading(true);
    setActionError(null);
    setBulkResult(null);

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/user/rooms/bulk-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Firebase-Token': token,
        },
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.error || '一括設定の更新に失敗しました。');
      }
      setBulkResult({
        updatedRoomCount: body.updatedRoomCount ?? 0,
        failures: body.failures ?? [],
      });
      // 部屋一覧を再取得して表示を最新化
      await fetchRooms();
      // 失敗が無い場合は自動でモーダルを閉じる
      if ((body.failures ?? []).length === 0) {
        setActiveModal(null);
        setSelectedRoomIds(new Set());
      }
    } catch (err: any) {
      console.error(err);
      setActionError(err.message || '一括設定の更新中にエラーが発生しました。');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <div className="theme-stage">
      <span className="theme-blob theme-blob--a" aria-hidden="true" />
      <span className="theme-blob theme-blob--b" aria-hidden="true" />
      <span className="theme-blob theme-blob--c" aria-hidden="true" />

      <div className="theme-stage__content">
        <section className="dashboard" aria-label="部屋管理">
          {/* Top bar (glass card) */}
          <header className="dashboard__topbar">
            <div className="dashboard__user-cluster">
              <HamburgerMenu
                header={{
                  icon: '👤',
                  label: user.email || 'ログイン中',
                  title: user.email || undefined,
                }}
                extraItems={[
                  {
                    icon: '📋',
                    label: 'テンプレート設定',
                    onClick: handleOpenTemplates,
                  },
                  {
                    icon: '🚫',
                    label: 'NGワード設定',
                    onClick: handleOpenBannedWords,
                  },
                ]}
                footerItems={[
                  {
                    icon: '🚪',
                    label: 'ログアウト',
                    onClick: handleLogout,
                    variant: 'danger',
                  },
                ]}
              />
            </div>
            <div className="dashboard__brand">
              <span className="dashboard__brand-avatar" aria-hidden="true">🤍</span>
              <h1 className="dashboard__brand-name">ChocoMesse</h1>
            </div>
          </header>

          {/* Main content */}
          <main className="dashboard__main">
            {/* Page header */}
            <div className="dashboard__page-header">
              <div className="dashboard__eyebrow">YOUR ROOMS</div>
              <div className="dashboard__title-row">
                <h2 className="dashboard__title">マイチャットルーム</h2>
                <div className="dashboard__count">♡ {rooms.length} rooms</div>
              </div>
              <div className="dashboard__actions">
                <button
                  type="button"
                  className="dashboard__action-secondary"
                  onClick={() => {
                    setActiveModal('join');
                    setActionError(null);
                  }}
                >
                  🔑 部屋に参加
                </button>
                <button
                  type="button"
                  className="dashboard__action-primary"
                  onClick={() => {
                    setActiveModal('create');
                    setActionError(null);
                  }}
                >
                  ＋ 新しい部屋
                </button>
              </div>
            </div>

            {/* 一括選択ツールバー: 常時表示。選択 0 件のとき「選択解除」と
                「一括設定」は disabled。「オーナールームを全選択」は常に押せる。 */}
            <div className="dashboard__bulk-toolbar">
              <div className="dashboard__bulk-info">
                {selectedRoomIds.size > 0 ? (
                  <>
                    <strong>{selectedRoomIds.size} 件</strong> の部屋を選択中
                  </>
                ) : (
                  <>オーナールームを選択して一括設定できます</>
                )}
              </div>
              <div className="dashboard__bulk-actions">
                <button
                  onClick={handleSelectAllOwned}
                  className="dashboard__bulk-btn dashboard__bulk-btn--secondary"
                  type="button"
                >
                  オーナールームを全選択
                </button>
                <button
                  onClick={handleClearSelection}
                  className="dashboard__bulk-btn dashboard__bulk-btn--secondary"
                  type="button"
                  disabled={selectedRoomIds.size === 0}
                >
                  選択解除
                </button>
                <button
                  onClick={handleOpenBulkSettings}
                  className="dashboard__bulk-btn dashboard__bulk-btn--primary"
                  type="button"
                  disabled={selectedRoomIds.size === 0}
                >
                  📢 一括設定
                </button>
              </div>
            </div>

            {error && <div className="dashboard__error" role="alert">{error}</div>}

            {/* Room list */}
            {loading ? (
              <div
                className="dashboard__rooms"
                role="list"
                aria-busy="true"
                aria-label="部屋一覧を読み込んでいます"
              >
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={`room-skeleton-${i}`}
                    className="dashboard__room-card dashboard__room-card--skeleton"
                    aria-hidden="true"
                  >
                    <span className="dashboard__room-checkbox-spacer" aria-hidden="true" />
                    <div className="dashboard__room-body">
                      <div className="dashboard__room-title-row">
                        <span className="dashboard__skeleton-line dashboard__skeleton-line--title" />
                      </div>
                      <span className="dashboard__skeleton-line dashboard__skeleton-line--preview" />
                    </div>
                    <div className="dashboard__room-meta">
                      <span className="dashboard__room-unread-placeholder" aria-hidden="true" />
                    </div>
                  </div>
                ))}
              </div>
            ) : rooms.length === 0 ? (
              <div className="dashboard__room-empty">
                <div className="dashboard__room-empty-icon" aria-hidden="true">💬</div>
                <h3 className="dashboard__room-empty-title">部屋がありません</h3>
                <p>
                  上のボタンから新しい部屋を作成するか、共有された合言葉を使って部屋に参加しましょう。
                </p>
              </div>
            ) : (
              <div className="dashboard__rooms" role="list">
                {rooms.map((room) => {
                  const isOwner = room.ownerUserId === user.uid;
                  const isSelected = selectedRoomIds.has(room.roomId);
                  // 表示するプレビューテキスト:
                  // announcement > 参加者リスト > 「待機中」のフォールバック。
                  const preview = room.announcement
                    ? room.announcement
                    : room.participants.length > 0
                      ? `参加者: ${room.participants.join(', ')}`
                      : '待機中';
                  const cardClassName = [
                    'dashboard__room-card',
                    room.isBlocked ? 'dashboard__room-card--blocked' : '',
                    isOwner ? 'dashboard__room-card--owner' : '',
                    isSelected ? 'dashboard__room-card--selected' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <div
                      key={room.roomId}
                      role="listitem"
                      className={cardClassName}
                      onClick={() => !room.isBlocked && handleRoomClick(room)}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && !room.isBlocked) {
                          e.preventDefault();
                          handleRoomClick(room);
                        }
                      }}
                      tabIndex={room.isBlocked ? -1 : 0}
                      aria-disabled={room.isBlocked ? true : undefined}
                      title={room.isBlocked ? 'ブロック解除するまで入室できません' : 'チャットを開く'}
                    >
                      {/* オーナーのみ複数選択チェックボックスを表示。
                          非オーナールームはスペーサーを置いて行の高さを揃える。 */}
                      {isOwner ? (
                        <input
                          type="checkbox"
                          className="dashboard__room-checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleRoomSelection(room)}
                          onClick={(e) => e.stopPropagation()}
                          title="一括設定の対象として選択"
                          aria-label={`${room.roomName} を一括設定対象として選択`}
                        />
                      ) : (
                        <span className="dashboard__room-checkbox-spacer" aria-hidden="true" />
                      )}

                      <div className="dashboard__room-body">
                        <div className="dashboard__room-title-row">
                          {isOwner && <span className="dashboard__room-pin" aria-hidden="true">📌</span>}
                          <h3 className="dashboard__room-name">{room.roomName}</h3>
                          {isOwner && (
                            <span className="dashboard__room-tag">オーナー</span>
                          )}
                          {room.isBlocked && (
                            <span className="dashboard__room-tag dashboard__room-tag--block">
                              ブロック
                            </span>
                          )}
                        </div>
                        <div className="dashboard__room-preview">{preview}</div>
                      </div>

                      <div className="dashboard__room-meta">
                        {room.unreadCount > 0 ? (
                          <div className="dashboard__room-unread" aria-label={`未読 ${room.unreadCount} 件`}>
                            {room.unreadCount}
                          </div>
                        ) : (
                          <span className="dashboard__room-unread-placeholder" aria-hidden="true" />
                        )}
                        <button
                          type="button"
                          className="dashboard__room-settings"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenSettings(room);
                          }}
                          aria-label={`${room.roomName} の設定を開く`}
                          title="部屋の設定"
                        >
                          ⚙️
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </main>
        </section>
      </div>

      {/* Modal Dialogs */}
      {activeModal && (
        <div style={styles.modalOverlay}>
          <div
            style={
              activeModal === 'templates' || activeModal === 'bulkSettings'
                ? { ...styles.modal, maxWidth: '640px' }
                : styles.modal
            }
          >
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                {activeModal === 'create' && '新しい部屋を作成'}
                {activeModal === 'join' && '合言葉で部屋に参加'}
                {activeModal === 'settings' && `部屋の設定: ${selectedRoom?.roomName}`}
                {activeModal === 'bannedWords' && 'NGワード設定 (全ルーム共通)'}
                {activeModal === 'templates' && 'テンプレートメッセージ設定 (全ルーム共通)'}
                {activeModal === 'bulkSettings' &&
                  `一括設定 (${selectedRoomIds.size} 件)`}
              </h3>
              <button
                onClick={() => { setActiveModal(null); setActionError(null); }}
                style={styles.modalCloseButton}
              >
                ✕
              </button>
            </div>

            {actionError && <div style={styles.modalError}>{actionError}</div>}

            <div style={styles.modalBody}>
              {/* CREATE OR JOIN ROOM FORM */}
              {(activeModal === 'create' || activeModal === 'join') && (
                <form
                  onSubmit={activeModal === 'create' ? handleCreateRoom : handleJoinRoom}
                  style={styles.modalForm}
                >
                  <div style={styles.field}>
                    <label style={styles.label}>部屋の表示名（マイページ用）</label>
                    <input
                      type="text"
                      value={roomNameInput}
                      onChange={(e) => setRoomNameInput(e.target.value)}
                      placeholder="例: 社外秘プロジェクトA"
                      disabled={actionLoading}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={styles.field}>
                    <label style={styles.label}>部屋の合言葉（パスワード）</label>
                    <input
                      type="password"
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                      placeholder="他のメンバーと共有する合言葉"
                      disabled={actionLoading}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={styles.field}>
                    <label style={styles.label}>あなたのニックネーム</label>
                    <input
                      type="text"
                      value={nicknameInput}
                      onChange={(e) => setNicknameInput(e.target.value)}
                      placeholder="例: タロウ"
                      disabled={actionLoading}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={styles.modalFooter}>
                    <button
                      type="button"
                      onClick={() => { setActiveModal(null); setActionError(null); }}
                      style={styles.modalButtonSecondary}
                      disabled={actionLoading}
                    >
                      キャンセル
                    </button>
                    <button
                      type="submit"
                      style={styles.modalButtonPrimary}
                      disabled={actionLoading}
                    >
                      {actionLoading ? '処理中...' : activeModal === 'create' ? '部屋を作成' : '部屋に参加'}
                    </button>
                  </div>
                </form>
              )}

              {/* SETTINGS FORM */}
              {activeModal === 'settings' && selectedRoom && (
                <div style={styles.modalForm}>
                  <div style={styles.checkboxField}>
                    <input
                      id="block-room"
                      type="checkbox"
                      checked={settingBlocked}
                      onChange={(e) => setSettingBlocked(e.target.checked)}
                      style={styles.checkbox}
                    />
                    <label htmlFor="block-room" style={styles.checkboxLabel}>
                      <strong>この部屋をブロックする</strong>
                      <span style={styles.checkboxHint}>
                        一覧の表示を保留にし、リアルタイム接続や通知を遮断します。
                      </span>
                    </label>
                  </div>

                  <div style={styles.checkboxField}>
                    <input
                      id="show-receipts"
                      type="checkbox"
                      checked={settingShowReceipt}
                      onChange={(e) => setSettingShowReceipt(e.target.checked)}
                      style={styles.checkbox}
                    />
                    <label htmlFor="show-receipts" style={styles.checkboxLabel}>
                      <strong>既読表示を有効にする</strong>
                      <span style={styles.checkboxHint}>
                        チャット画面でメッセージを読んだときに、相手に既読マークを表示します。
                      </span>
                    </label>
                  </div>

                  {/* Announcement + Guest message limit (Owner only) */}
                  {selectedRoom.ownerUserId === user.uid ? (
                    <>
                      <div style={styles.field}>
                        <label style={styles.label}>お知らせメッセージの編集 (オーナー機能)</label>
                        <textarea
                          value={settingAnnouncement}
                          onChange={(e) => setSettingAnnouncement(e.target.value)}
                          placeholder="チャットルーム上部に表示する常時お知らせメッセージ（空欄で非表示）"
                          style={styles.textarea}
                          rows={3}
                        />
                      </div>
                      <div style={styles.field}>
                        <label style={styles.label}>
                          ゲストの連続メッセージ制限 (オーナー機能)
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={settingGuestLimit}
                          onChange={(e) => setSettingGuestLimit(e.target.value)}
                          placeholder="0"
                          style={styles.input}
                        />
                        <div style={styles.checkboxHint}>
                          ゲストはオーナーの返信が無い間に連続で送れるメッセージ数の上限です。<strong>0 を指定すると制限なし</strong>になります。オーナー自身は何回でも送信できます。
                        </div>
                      </div>
                    </>
                  ) : (
                    selectedRoom.announcement && (
                      <div style={styles.readOnlyAnnouncement}>
                        <strong>設定中のお知らせ:</strong>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>{selectedRoom.announcement}</p>
                      </div>
                    )
                  )}

                  {/* Red Danger Zone for delete / leave */}
                  <div style={styles.dangerZone}>
                    <h4 style={styles.dangerZoneTitle}>危険エリア</h4>
                    
                    {!showDeleteConfirm ? (
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(true)}
                        style={styles.dangerButton}
                      >
                        {selectedRoom.ownerUserId === user.uid ? '部屋を完全に削除する' : 'この部屋から退室する'}
                      </button>
                    ) : (
                      <div style={styles.deleteConfirmBox}>
                        <p style={styles.deleteConfirmWarning}>
                          ⚠️ <strong>本当に実行しますか？</strong>
                          <br />
                          {selectedRoom.ownerUserId === user.uid
                            ? 'この操作は取り消せません。部屋のデータ、およびチャット履歴・メッセージが完全に消去されます。'
                            : 'この部屋の登録を解除して退室します。再度入室するには合言葉が必要です。'}
                        </p>
                        <div style={styles.deleteConfirmActions}>
                          <button
                            type="button"
                            onClick={() => setShowDeleteConfirm(false)}
                            style={styles.modalButtonSecondary}
                            disabled={actionLoading}
                          >
                            キャンセル
                          </button>
                          <button
                            type="button"
                            onClick={handleDeleteRoom}
                            style={styles.confirmDeleteButton}
                            disabled={actionLoading}
                          >
                            {actionLoading ? '処理中...' : selectedRoom.ownerUserId === user.uid ? '完全に削除する' : '退室する'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={styles.modalFooter}>
                    <button
                      type="button"
                      onClick={() => { setActiveModal(null); setActionError(null); }}
                      style={styles.modalButtonSecondary}
                      disabled={actionLoading}
                    >
                      キャンセル
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveSettings}
                      style={styles.modalButtonPrimary}
                      disabled={actionLoading}
                    >
                      {actionLoading ? '保存中...' : '保存'}
                    </button>
                  </div>
                </div>
              )}

              {/* GLOBAL TEMPLATES FORM */}
              {activeModal === 'templates' && (
                <div style={styles.modalForm}>
                  <div style={styles.checkboxHint}>
                    ここで登録したテンプレートは、<strong>あなたが作成した全ての部屋</strong>のメッセージ入力欄から呼び出せます。1件あたり最大2000文字、複数行可。最大50件まで登録できます。
                  </div>

                  {templatesLoading ? (
                    <div style={styles.checkboxHint}>読み込み中...</div>
                  ) : (
                    <>
                      <div style={templateStyles.list}>
                        {globalTemplates.length === 0 && editingTemplateIndex !== -1 && (
                          <div style={templateStyles.emptyHint}>
                            まだテンプレートが登録されていません。下の「＋ 新規追加」ボタンから登録できます。
                          </div>
                        )}
                        {globalTemplates.map((tpl, idx) => (
                          <div key={idx} style={templateStyles.item}>
                            {editingTemplateIndex === idx ? (
                              <div style={templateStyles.editorBox}>
                                <textarea
                                  value={editingTemplateText}
                                  onChange={(e) => setEditingTemplateText(e.target.value)}
                                  style={styles.textarea}
                                  rows={6}
                                  autoFocus
                                  placeholder="テンプレートの本文を入力（複数行可）"
                                />
                                <div style={templateStyles.charCount}>
                                  {editingTemplateText.length} / 2000
                                </div>
                                <div style={templateStyles.editorActions}>
                                  <button
                                    type="button"
                                    onClick={handleCancelEditTemplate}
                                    style={styles.modalButtonSecondary}
                                    disabled={actionLoading}
                                  >
                                    キャンセル
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleApplyEditingTemplate}
                                    style={styles.modalButtonPrimary}
                                    disabled={actionLoading}
                                  >
                                    適用
                                  </button>
                                </div>
                              </div>
                            ) : deleteTemplateIndex === idx ? (
                              <div style={templateStyles.deleteConfirm}>
                                <div style={templateStyles.itemPreview}>{tpl}</div>
                                <div style={templateStyles.deleteWarn}>
                                  ⚠️ このテンプレートを削除しますか？
                                </div>
                                <div style={templateStyles.editorActions}>
                                  <button
                                    type="button"
                                    onClick={handleCancelDeleteTemplate}
                                    style={styles.modalButtonSecondary}
                                    disabled={actionLoading}
                                  >
                                    キャンセル
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleConfirmDeleteTemplate}
                                    style={styles.confirmDeleteButton}
                                    disabled={actionLoading}
                                  >
                                    削除
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div style={templateStyles.itemPreview}>{tpl}</div>
                                <div style={templateStyles.itemActions}>
                                  <button
                                    type="button"
                                    onClick={() => handleStartEditTemplate(idx)}
                                    style={templateStyles.smallButton}
                                    disabled={actionLoading || editingTemplateIndex !== null}
                                    title="編集"
                                  >
                                    ✏️ 編集
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleAskDeleteTemplate(idx)}
                                    style={templateStyles.smallDangerButton}
                                    disabled={actionLoading || editingTemplateIndex !== null}
                                    title="削除"
                                  >
                                    🗑 削除
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}

                        {editingTemplateIndex === -1 && (
                          <div style={templateStyles.item}>
                            <div style={templateStyles.editorBox}>
                              <textarea
                                value={editingTemplateText}
                                onChange={(e) => setEditingTemplateText(e.target.value)}
                                style={styles.textarea}
                                rows={6}
                                autoFocus
                                placeholder="新規テンプレートの本文を入力（複数行可）"
                              />
                              <div style={templateStyles.charCount}>
                                {editingTemplateText.length} / 2000
                              </div>
                              <div style={templateStyles.editorActions}>
                                <button
                                  type="button"
                                  onClick={handleCancelEditTemplate}
                                  style={styles.modalButtonSecondary}
                                  disabled={actionLoading}
                                >
                                  キャンセル
                                </button>
                                <button
                                  type="button"
                                  onClick={handleApplyEditingTemplate}
                                  style={styles.modalButtonPrimary}
                                  disabled={actionLoading}
                                >
                                  追加
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {editingTemplateIndex === null && globalTemplates.length < 50 && (
                        <div>
                          <button
                            type="button"
                            onClick={handleStartAddTemplate}
                            style={templateStyles.addButton}
                            disabled={actionLoading}
                          >
                            ＋ 新規テンプレートを追加
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  <div style={styles.modalFooter}>
                    <button
                      type="button"
                      onClick={() => { setActiveModal(null); setActionError(null); }}
                      style={styles.modalButtonSecondary}
                      disabled={actionLoading}
                    >
                      閉じる
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveTemplates}
                      style={styles.modalButtonPrimary}
                      disabled={actionLoading || templatesLoading || editingTemplateIndex !== null}
                      title={
                        editingTemplateIndex !== null
                          ? '編集中のテンプレートを適用またはキャンセルしてください'
                          : undefined
                      }
                    >
                      {actionLoading ? '保存中...' : '保存'}
                    </button>
                  </div>
                </div>
              )}

              {/* BULK SETTINGS FORM
                  選択中の複数ルームに対して「お知らせメッセージ」「ゲストの
                  連続メッセージ制限」を一括設定する。各フィールドは「適用する」
                  チェックボックスで明示的に有効化する設計（誤って意図せず
                  上書きされないようにするため）。 */}
              {activeModal === 'bulkSettings' && (
                <div style={styles.modalForm}>
                  <div style={styles.checkboxHint}>
                    選択中の <strong>{selectedRoomIds.size} 件</strong> の部屋に対して、お知らせメッセージとゲストの連続メッセージ制限を一括で設定します。<strong>「適用する」を選んだ項目のみ</strong>更新され、未選択の項目は各ルームの現在値を維持します。
                  </div>

                  {/* 対象ルームのプレビュー（最大5件まで表示し残りは件数表示） */}
                  <div style={bulkStyles.targetList}>
                    {rooms
                      .filter((r) => selectedRoomIds.has(r.roomId))
                      .slice(0, 5)
                      .map((r) => (
                        <span key={r.roomId} style={bulkStyles.targetChip}>
                          {r.roomName}
                        </span>
                      ))}
                    {selectedRoomIds.size > 5 && (
                      <span style={bulkStyles.targetMore}>
                        他 {selectedRoomIds.size - 5} 件
                      </span>
                    )}
                  </div>

                  {/* お知らせメッセージ */}
                  <div style={bulkStyles.fieldGroup}>
                    <label style={bulkStyles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={bulkApplyAnnouncement}
                        onChange={(e) => setBulkApplyAnnouncement(e.target.checked)}
                        style={styles.checkbox}
                      />
                      <strong>お知らせメッセージを一括設定する</strong>
                    </label>
                    <textarea
                      value={bulkAnnouncement}
                      onChange={(e) => setBulkAnnouncement(e.target.value)}
                      placeholder="チャットルーム上部に表示する常時お知らせメッセージ（空欄で非表示）"
                      style={{
                        ...styles.textarea,
                        ...(bulkApplyAnnouncement ? {} : bulkStyles.disabledInput),
                      }}
                      rows={3}
                      disabled={!bulkApplyAnnouncement || actionLoading}
                    />
                    <div style={styles.checkboxHint}>
                      ※ 各ルームのお知らせを同じ内容に上書きします。空欄にすると全選択ルームのお知らせが非表示になります。
                    </div>
                  </div>

                  {/* ゲストの連続メッセージ制限（返信数） */}
                  <div style={bulkStyles.fieldGroup}>
                    <label style={bulkStyles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={bulkApplyGuestLimit}
                        onChange={(e) => setBulkApplyGuestLimit(e.target.checked)}
                        style={styles.checkbox}
                      />
                      <strong>ゲストの連続メッセージ制限 (返信数) を一括設定する</strong>
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={bulkGuestLimit}
                      onChange={(e) => setBulkGuestLimit(e.target.value)}
                      placeholder="0"
                      style={{
                        ...styles.input,
                        ...(bulkApplyGuestLimit ? {} : bulkStyles.disabledInput),
                      }}
                      disabled={!bulkApplyGuestLimit || actionLoading}
                    />
                    <div style={styles.checkboxHint}>
                      ゲストがオーナーの返信無しに連続で送れるメッセージ数の上限。<strong>0 で制限なし</strong>。制限変更時は各ルームの連続メッセージカウンタが 0 にリセットされます。
                    </div>
                  </div>

                  {/* 直近実行結果（部分失敗時のみ画面に残してフィードバック） */}
                  {bulkResult && bulkResult.failures.length > 0 && (
                    <div style={bulkStyles.resultBox}>
                      <strong>
                        {bulkResult.updatedRoomCount} 件の部屋を更新しました。
                      </strong>
                      <div style={{ marginTop: '6px' }}>
                        ただし以下 {bulkResult.failures.length} 件は更新に失敗しています:
                      </div>
                      <ul style={bulkStyles.failureList}>
                        {bulkResult.failures.map((f) => {
                          const r = rooms.find((x) => x.roomId === f.roomId);
                          const name = r?.roomName ?? f.roomId;
                          const reasonLabel =
                            f.reason === 'not_owner'
                              ? 'オーナー権限なし'
                              : f.reason === 'not_found'
                                ? '部屋が見つかりません'
                                : '内部エラー';
                          return (
                            <li key={f.roomId}>
                              {name} <small>({reasonLabel})</small>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  <div style={styles.modalFooter}>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveModal(null);
                        setActionError(null);
                        setBulkResult(null);
                      }}
                      style={styles.modalButtonSecondary}
                      disabled={actionLoading}
                    >
                      閉じる
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveBulkSettings}
                      style={styles.modalButtonPrimary}
                      disabled={
                        actionLoading ||
                        (!bulkApplyAnnouncement && !bulkApplyGuestLimit)
                      }
                    >
                      {actionLoading
                        ? '適用中...'
                        : `${selectedRoomIds.size} 件に適用`}
                    </button>
                  </div>
                </div>
              )}

              {/* GLOBAL BANNED WORDS FORM */}
              {activeModal === 'bannedWords' && (
                <div style={styles.modalForm}>
                  <div style={styles.field}>
                    <label style={styles.label}>NGワード一覧</label>
                    {bannedWordsLoading ? (
                      <div style={styles.checkboxHint}>読み込み中...</div>
                    ) : (
                      <textarea
                        value={globalBannedWords}
                        onChange={(e) => setGlobalBannedWords(e.target.value)}
                        placeholder="1行に1ワード&#10;例:&#10;禁止語1&#10;禁止語2"
                        style={styles.textarea}
                        rows={8}
                        disabled={actionLoading}
                      />
                    )}
                    <div style={styles.checkboxHint}>
                      ここに登録した単語が含まれるメッセージは送信できなくなります。<strong>あなたが作成した全ての部屋に共通で適用</strong>されます。1行に1ワード、最大50件、1件あたり50文字以内。大文字小文字は区別されず、部分一致で判定されます。
                    </div>
                  </div>

                  <div style={styles.modalFooter}>
                    <button
                      type="button"
                      onClick={() => { setActiveModal(null); setActionError(null); }}
                      style={styles.modalButtonSecondary}
                      disabled={actionLoading}
                    >
                      キャンセル
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveBannedWords}
                      style={styles.modalButtonPrimary}
                      disabled={actionLoading || bannedWordsLoading}
                    >
                      {actionLoading ? '保存中...' : '保存'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 一括設定モーダル専用のスタイル群（既存 `styles` には依存しつつ、モーダル
// 内で完結する装飾のみここに切り出す）。
const bulkStyles: Record<string, React.CSSProperties> = {
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '12px',
    border: '1px solid var(--line)',
    borderRadius: '4px',
    background: 'var(--bg-plain)',
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: 'var(--ink)',
    cursor: 'pointer',
  },
  disabledInput: {
    background: 'var(--surface-2)',
    color: 'var(--ink-mute)',
  },
  targetList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    padding: '8px',
    background: 'var(--surface-2)',
    borderRadius: '4px',
  },
  targetChip: {
    display: 'inline-block',
    background: 'var(--surface)',
    border: '1px solid var(--line)',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    color: 'var(--ink)',
  },
  targetMore: {
    display: 'inline-block',
    padding: '3px 8px',
    fontSize: '12px',
    color: 'var(--ink-mute)',
    fontStyle: 'italic',
  },
  resultBox: {
    background: '#FFFAE6',
    border: '1px solid #FFAB00',
    borderRadius: '4px',
    padding: '12px',
    color: '#974F0C',
    fontSize: '13px',
    lineHeight: 1.5,
  },
  failureList: {
    margin: '6px 0 0 18px',
    padding: 0,
    fontSize: '13px',
  },
};

// テンプレート CRUD 用のスタイル群（既存 `styles` から独立させ、共通ルックの
// 上に CRUD 特有の要素だけを差し込む方針）。
const templateStyles: Record<string, React.CSSProperties> = {
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '50vh',
    overflowY: 'auto',
    border: '1px solid var(--line)',
    borderRadius: '4px',
    padding: '8px',
    background: 'var(--bg-plain)',
  },
  emptyHint: {
    padding: '20px',
    textAlign: 'center',
    color: 'var(--ink-mute)',
    fontSize: '13px',
  },
  item: {
    background: 'var(--surface)',
    border: '1px solid var(--line)',
    borderRadius: '4px',
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  itemPreview: {
    fontSize: '13px',
    color: 'var(--ink)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    lineHeight: '1.5',
    background: 'var(--surface-2)',
    padding: '8px 10px',
    borderRadius: '3px',
    maxHeight: '160px',
    overflowY: 'auto',
  },
  itemActions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  smallButton: {
    background: 'var(--surface)',
    color: 'var(--primary)',
    border: '1px solid var(--primary)',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: 600,
    borderRadius: '3px',
    cursor: 'pointer',
  },
  smallDangerButton: {
    background: 'var(--surface)',
    color: '#DE350B',
    border: '1px solid #DE350B',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: 600,
    borderRadius: '3px',
    cursor: 'pointer',
  },
  editorBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  charCount: {
    fontSize: '11px',
    color: 'var(--ink-mute)',
    textAlign: 'right',
  },
  editorActions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  deleteConfirm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    background: '#FFEBE6',
    border: '1px solid #FFBDAD',
    borderRadius: '3px',
    padding: '8px',
  },
  deleteWarn: {
    fontSize: '13px',
    color: '#BF2600',
    fontWeight: 600,
  },
  addButton: {
    background: 'var(--bg-plain)',
    color: 'var(--primary)',
    border: '2px dashed var(--primary)',
    padding: '10px 16px',
    fontSize: '13px',
    fontWeight: 600,
    borderRadius: '4px',
    cursor: 'pointer',
    width: '100%',
  },
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'transparent',
    display: 'flex',
    flexDirection: 'column' as const,
    fontFamily: 'var(--font-sans)',
  },
  header: {
    margin: '12px 12px 0',
    padding: '12px 16px',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--card-bg)',
    border: 'var(--card-border)',
    boxShadow: 'var(--card-shadow)',
    backdropFilter: 'var(--card-backdrop)',
    WebkitBackdropFilter: 'var(--card-backdrop)',
    color: 'var(--ink)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    position: 'relative' as const,
    zIndex: 1,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  brandName: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 700,
    color: 'var(--ink)',
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap' as const,
  },
  logoIcon: {
    fontSize: '16px',
    display: 'inline-flex',
    width: '34px',
    height: '34px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-md)',
    background: 'var(--avatar-grad)',
    color: '#FFFFFF',
    boxShadow: '0 4px 12px rgba(200, 149, 106, 0.27)',
  } as React.CSSProperties,
  userSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  userInfo: {
    fontSize: '14px',
    opacity: 0.9,
  },
  headerButton: {
    background: 'rgba(255, 255, 255, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.35)',
    color: '#FFFFFF',
    padding: '6px 12px',
    fontSize: '13px',
    fontWeight: 600,
    borderRadius: '3px',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  },
  logoutButton: {
    background: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    color: '#FFFFFF',
    padding: '6px 12px',
    fontSize: '13px',
    fontWeight: 600,
    borderRadius: '3px',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    ':hover': {
      background: 'rgba(255, 255, 255, 0.3)',
    },
  },
  main: {
    flex: 1,
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    padding: '32px 24px',
    boxSizing: 'border-box' as const,
  },
  dashboardActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    color: 'var(--ink)',
  },
  actionButtons: {
    display: 'flex',
    gap: '12px',
  },
  buttonPrimary: {
    background: 'var(--primary-grad)',
    color: 'var(--on-primary)',
    border: 'none',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: 700,
    borderRadius: 'var(--radius-button)',
    cursor: 'pointer',
    transition: 'filter 0.15s ease',
    boxShadow: '0 6px 18px rgba(200, 149, 106, 0.35)',
  },
  buttonSecondary: {
    background: 'var(--card-bg)',
    color: 'var(--primary-dark)',
    border: 'var(--card-border)',
    backdropFilter: 'var(--card-backdrop)',
    WebkitBackdropFilter: 'var(--card-backdrop)',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: 700,
    borderRadius: 'var(--radius-button)',
    cursor: 'pointer',
    transition: 'filter 0.15s ease',
  } as React.CSSProperties,
  errorBanner: {
    background: '#FFEBE6',
    border: '1px solid #FF5230',
    borderRadius: '4px',
    padding: '12px',
    color: '#BF2600',
    fontSize: '14px',
    marginBottom: '20px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 0',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid var(--line)',
    borderTop: '3px solid var(--primary)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    color: 'var(--ink-mute)',
    fontSize: '14px',
  },
  emptyContainer: {
    background: 'var(--surface)',
    border: '2px dashed var(--line)',
    borderRadius: '8px',
    padding: '60px 20px',
    textAlign: 'center' as const,
    maxWidth: '500px',
    margin: '40px auto 0 auto',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyTitle: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    color: 'var(--ink)',
  },
  emptyDesc: {
    margin: 0,
    fontSize: '14px',
    color: 'var(--ink-mute)',
    lineHeight: '1.5',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '20px',
  },
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--line)',
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(9, 30, 66, 0.08)',
    display: 'flex',
    flexDirection: 'column' as const,
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    overflow: 'hidden',
  },
  cardBlocked: {
    background: 'var(--bg-plain)',
    opacity: 0.7,
  },
  cardSelected: {
    borderColor: 'var(--primary)',
    boxShadow: '0 0 0 2px rgba(0, 82, 204, 0.25)',
    background: 'var(--surface-2)',
  },
  cardHeader: {
    padding: '16px 20px 8px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '10px',
  },
  selectCheckbox: {
    marginTop: '4px',
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  selectCheckboxSpacer: {
    display: 'inline-block',
    width: '18px',
    height: '18px',
    marginTop: '4px',
    flexShrink: 0,
  },
  selectionToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'var(--surface-2)',
    border: '1px solid var(--primary)',
    borderRadius: '4px',
    padding: '10px 16px',
    marginBottom: '16px',
    gap: '12px',
    flexWrap: 'wrap' as const,
  },
  selectionInfo: {
    fontSize: '14px',
    color: 'var(--primary-deep)',
  },
  selectionActions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap' as const,
  },
  toolbarButtonPrimary: {
    background: 'var(--primary)',
    color: '#FFFFFF',
    border: 'none',
    padding: '6px 14px',
    fontSize: '13px',
    fontWeight: 600,
    borderRadius: '3px',
    cursor: 'pointer',
  },
  toolbarButtonSecondary: {
    background: 'var(--surface)',
    color: 'var(--primary)',
    border: '1px solid var(--primary)',
    padding: '6px 14px',
    fontSize: '13px',
    fontWeight: 600,
    borderRadius: '3px',
    cursor: 'pointer',
  },
  cardHeaderClickable: {
    flex: 1,
    cursor: 'pointer',
  },
  roomName: {
    margin: '0 0 4px 0',
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--ink)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap' as const,
  },
  blockedBadge: {
    fontSize: '10px',
    background: '#FFEBE6',
    color: '#BF2600',
    padding: '2px 6px',
    borderRadius: '3px',
    fontWeight: 'bold',
  },
  ownerBadge: {
    fontSize: '10px',
    background: '#EAE6FF',
    color: '#403294',
    padding: '2px 6px',
    borderRadius: '3px',
    fontWeight: 'bold',
  },
  nicknameLabel: {
    fontSize: '13px',
    color: 'var(--ink-mute)',
  },
  settingsButton: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    transition: 'background-color 0.15s ease',
    ':hover': {
      background: 'var(--line-soft)',
    },
  },
  cardBody: {
    padding: '8px 20px 16px 20px',
    flex: 1,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
  },
  announcementSnippet: {
    background: 'var(--surface-2)',
    color: 'var(--primary-deep)',
    fontSize: '12px',
    padding: '8px 12px',
    borderRadius: '4px',
    lineHeight: '1.4',
    wordBreak: 'break-all' as const,
  },
  participantsList: {
    fontSize: '13px',
    color: 'var(--ink-mute)',
    fontStyle: 'italic',
  },
  cardFooter: {
    borderTop: '1px solid var(--surface-2)',
    padding: '12px 20px',
    background: 'var(--bg-plain)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statusInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  unreadBadge: {
    background: '#DE350B',
    color: '#FFFFFF',
    fontSize: '11px',
    fontWeight: 'bold',
    padding: '2px 8px',
    borderRadius: '10px',
  },
  readLabel: {
    fontSize: '12px',
    color: '#36B37E',
    fontWeight: 600,
  },
  enterButton: {
    background: 'none',
    border: 'none',
    color: 'var(--primary)',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
    ':hover': {
      color: 'var(--primary-dark)',
      textDecoration: 'underline',
    },
  },
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    /* 共通の不透明オーバーレイ。sage テーマでも背景が確実に暗くなるよう
     * --popup-overlay (テーマ依存の濃いめ単色) を使う。 */
    background: 'var(--popup-overlay)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100,
    padding: '20px',
  },
  modal: {
    /* var(--surface) は sage テーマで rgba(255, 255, 255, 0.55) と
     * 半透明になりモーダル背景がチャットや一覧と混ざってしまう。
     * ポップアップ専用の常時不透明トークンに差し替える。 */
    background: 'var(--popup-bg)',
    borderRadius: '4px',
    boxShadow: 'var(--popup-shadow)',
    width: '100%',
    maxWidth: '480px',
    display: 'flex',
    flexDirection: 'column' as const,
    maxHeight: '90vh',
    overflowY: 'auto' as const,
  },
  modalHeader: {
    padding: '18px 24px',
    borderBottom: '2px solid var(--surface-2)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: 'var(--ink)',
  },
  modalCloseButton: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    color: 'var(--ink-mute)',
    padding: '4px',
  },
  modalError: {
    background: '#FFEBE6',
    borderLeft: '4px solid #FF5230',
    padding: '12px 20px',
    color: '#BF2600',
    fontSize: '14px',
  },
  modalBody: {
    padding: '24px',
  },
  modalForm: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--ink-mute)',
  },
  input: {
    padding: '8px 12px',
    fontSize: '14px',
    border: '2px solid var(--line)',
    borderRadius: '4px',
    background: 'var(--bg-plain)',
    color: 'var(--ink)',
    outline: 'none',
    boxSizing: 'border-box' as const,
    ':focus': {
      background: 'var(--surface)',
      borderColor: '#4C9AFF',
    },
  },
  textarea: {
    padding: '8px 12px',
    fontSize: '14px',
    border: '2px solid var(--line)',
    borderRadius: '4px',
    background: 'var(--bg-plain)',
    color: 'var(--ink)',
    outline: 'none',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  },
  checkboxField: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
  },
  checkbox: {
    marginTop: '3px',
    cursor: 'pointer',
  },
  checkboxLabel: {
    display: 'flex',
    flexDirection: 'column' as const,
    cursor: 'pointer',
    fontSize: '14px',
    color: 'var(--ink)',
  },
  checkboxHint: {
    fontSize: '12px',
    color: 'var(--ink-mute)',
    marginTop: '2px',
  },
  readOnlyAnnouncement: {
    background: 'var(--surface-2)',
    padding: '12px',
    borderRadius: '4px',
    color: 'var(--ink-soft)',
  },
  dangerZone: {
    border: '1px solid #FFBDAD',
    borderRadius: '4px',
    background: '#FFEBE6',
    padding: '16px',
    marginTop: '10px',
  },
  dangerZoneTitle: {
    margin: '0 0 12px 0',
    color: '#BF2600',
    fontSize: '14px',
    fontWeight: 600,
  },
  dangerButton: {
    background: '#FF5230',
    color: '#FFFFFF',
    border: 'none',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 600,
    borderRadius: '3px',
    cursor: 'pointer',
    transition: 'background-color 0.1s ease',
    ':hover': {
      background: '#DE350B',
    },
  },
  deleteConfirmBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  deleteConfirmWarning: {
    margin: 0,
    fontSize: '13px',
    color: '#BF2600',
    lineHeight: '1.5',
  },
  deleteConfirmActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
  },
  confirmDeleteButton: {
    background: '#DE350B',
    color: '#FFFFFF',
    border: 'none',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 600,
    borderRadius: '3px',
    cursor: 'pointer',
    ':hover': {
      background: '#BF2600',
    },
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '10px',
    borderTop: '2px solid var(--surface-2)',
    paddingTop: '16px',
  },
  modalButtonPrimary: {
    background: 'var(--primary)',
    color: '#FFFFFF',
    border: 'none',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 600,
    borderRadius: '4px',
    cursor: 'pointer',
    ':hover': {
      background: 'var(--primary-dark)',
    },
  },
  modalButtonSecondary: {
    background: 'none',
    border: 'none',
    color: 'var(--ink-soft)',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    ':hover': {
      color: 'var(--ink)',
    },
  },
};
